// The list of the Unassigned voices tab (ADR-0077): the voices nobody is confirmed on, grouped
// under their recording, each with its clip and whatever the matcher proposes. Pure — it
// renders what it is given and reports the ticks; the fetch lives in `useUnassignedVoices`,
// the player is handed in per row.
import { AudioLines } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { formatSimilarity } from '@/components/humint/format'
import { ShortVoiceEvidence } from '@/components/humint/voice/ShortVoiceEvidence'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'
import { documentPath } from '@/features/documents/document-path'
import type { UnassignedVoice } from '@/services/api/identity-voices'
import { cn } from '@/utils/cn'
import { groupByRecording, unassignableReason } from './unassigned-voices'
import { SpeechSeconds, VoiceWhen } from './voice-facts'

const COPY = 'identity.unassignedVoices'

/** The analyst's ticks; without it the rows have no checkbox: a viewer reads and listens. */
export interface VoiceSelection {
  isSelected: (voiceprintId: string) => boolean
  onToggle: (voiceprintId: string) => void
}

export interface UnassignedVoicesListProps {
  caseId: string
  voices: UnassignedVoice[]
  isLoading: boolean
  selection?: VoiceSelection
  /** The voice's player, connected by the host; nothing for a voice with no clip. */
  player: (voice: UnassignedVoice) => ReactNode
}

function Suggestion({ voice }: { voice: UnassignedVoice }) {
  const { t, i18n } = useTranslation()
  const best = voice.best_suggestion
  if (!best) return null
  const more = voice.pending_suggestions - 1
  return (
    <p className="flex flex-wrap items-center gap-2 text-sm">
      <Badge variant="secondary">{t(`${COPY}.suggestion`, { name: best.name ?? '' })}</Badge>
      {best.similarity !== null ? (
        <span className="text-muted-foreground">
          {t('humint.voice.lead.similarity')} {formatSimilarity(best.similarity, i18n.language)}
        </span>
      ) : null}
      {more > 0 ? (
        <span className="text-muted-foreground">
          {t(`${COPY}.moreSuggestions`, { count: more })}
        </span>
      ) : null}
    </p>
  )
}

function VoiceRow({
  voice,
  selection,
  player,
}: {
  voice: UnassignedVoice
  selection?: VoiceSelection
  player: ReactNode
}) {
  const { t } = useTranslation()
  const reason = unassignableReason(voice)
  const selected =
    voice.voiceprint_id !== null && Boolean(selection?.isSelected(voice.voiceprint_id))
  const tickable = voice.voiceprint_id !== null && reason === null
  return (
    <li
      data-voice-id={voice.voice_id}
      className={cn(
        'grid gap-3 px-4 py-4 sm:grid-cols-[auto_minmax(0,1fr)_minmax(14rem,20rem)] sm:items-start',
        selected && 'bg-primary/[0.06]'
      )}
    >
      <div className="flex h-6 items-center">
        {selection ? (
          <Checkbox
            checked={selected}
            disabled={!tickable}
            aria-label={t(`${COPY}.selectVoice`, { voice: voice.voice })}
            onCheckedChange={() => voice.voiceprint_id && selection.onToggle(voice.voiceprint_id)}
          />
        ) : null}
      </div>
      <div className="min-w-0 space-y-1.5">
        <h3 className="font-medium">{t(`${COPY}.voice`, { voice: voice.voice })}</h3>
        <SpeechSeconds seconds={voice.speech_seconds} />
        {reason ? <p className="text-sm text-muted-foreground">{t(`${COPY}.${reason}`)}</p> : null}
        {voice.short_evidence ? <ShortVoiceEvidence /> : null}
        <Suggestion voice={voice} />
        {voice.attribution_conflict ? (
          <p className="text-sm text-warning-foreground">{t(`${COPY}.conflict`)}</p>
        ) : null}
      </div>
      <div className="w-full sm:flex sm:justify-end">{player}</div>
    </li>
  )
}

export function UnassignedVoicesList({
  caseId,
  voices,
  isLoading,
  selection,
  player,
}: UnassignedVoicesListProps) {
  const { t } = useTranslation()
  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }, (_, index) => (
          <Skeleton key={`voice-skeleton-${index}`} className="h-24 w-full rounded-lg" />
        ))}
      </div>
    )
  }
  if (voices.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-border py-12 text-center text-sm text-muted-foreground">
        {t(`${COPY}.empty`)}
      </p>
    )
  }
  return (
    <div className="space-y-4">
      {groupByRecording(voices).map(group => (
        <section
          key={group.documentId}
          aria-label={group.documentName ?? group.documentId}
          className="overflow-hidden rounded-lg border border-border bg-card"
        >
          <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-muted/20 px-4 py-3">
            <div className="flex min-w-0 items-center gap-2">
              <AudioLines aria-hidden className="size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium" title={group.documentName ?? undefined}>
                  {group.documentName ?? t('identity.faces.unknownSource')}
                </p>
                <VoiceWhen iso={group.recordedAt} />
              </div>
            </div>
            <Button variant="outline" size="xs" asChild>
              <Link to={documentPath(caseId, group.documentId)}>
                {t('identity.detail.audio.openRecording')}
              </Link>
            </Button>
          </header>
          <ul className="divide-y divide-border/60">
            {group.voices.map(voice => (
              <VoiceRow
                key={voice.voice_id}
                voice={voice}
                selection={selection}
                player={player(voice)}
              />
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}
